import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Title,
  Table,
  Badge,
  TextInput,
  Select,
  Group,
  Text,
  Stack,
  ActionIcon,
  Tooltip,
  Button,
  Modal,
} from "@mantine/core";
import { IconEye, IconSearch, IconCategory, IconDownload, IconFileText } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { adminVendorsApi, AdminVendorDocument } from "../../../api/adminVendors";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

export function VendorManagement() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [documentsModalOpened, setDocumentsModalOpened] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [selectedVendorName, setSelectedVendorName] = useState<string>("");

  const { data: vendorDetails } = useQuery({
    queryKey: ["admin", "vendor", selectedVendor],
    queryFn: () => adminVendorsApi.getVendor(selectedVendor!),
    enabled: !!selectedVendor && documentsModalOpened,
  });

  const { data: vendors, isLoading } = useQuery({
    queryKey: ["admin", "vendors", statusFilter, searchQuery],
    queryFn: () =>
      adminVendorsApi.listVendors({
        status: statusFilter === "ALL" ? undefined : (statusFilter as "REGISTERED" | "PENDING" | "ACTIVE" | "SUSPENDED"),
        search: searchQuery || undefined,
      }),
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "PENDING":
        return "yellow";
      case "REGISTERED":
        return "blue";
      case "SUSPENDED":
        return "red";
      default:
        return "gray";
    }
  };

  const getBusinessTypeLabel = (type: string | null) => {
    if (!type) return "Not specified";
    const labels: Record<string, string> = {
      SOLE_PROPRIETOR: "Sole Proprietor",
      COMPANY: "Company",
      PARTNERSHIP: "Partnership",
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      BUSINESS_REGISTRATION: "Business Registration",
      COMPANY_PROFILE: "Company Profile",
      TAX_CLEARANCE: "Tax Clearance",
      INSURANCE: "Insurance",
      FINANCIAL_STATEMENT: "Financial Statement",
      OTHER: "Other",
    };
    return labels[type] || type;
  };

  const handleViewDocuments = (vendorId: string, vendorName: string) => {
    setSelectedVendor(vendorId);
    setSelectedVendorName(vendorName);
    setDocumentsModalOpened(true);
  };

  const handleDownloadDocument = async (vendorId: string, doc: AdminVendorDocument) => {
    try {
      const blob = await adminVendorsApi.downloadDocument(vendorId, doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notifications.show({
        title: "Success",
        message: "Document downloaded successfully",
        color: "green",
      });
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to download document",
        color: "red",
      });
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Vendor Management</Title>
          <Button
            leftSection={<IconCategory size={16} />}
            onClick={() => navigate("/admin/vendors/categories")}
            color="green"
            variant="light"
          >
            Manage Categories
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="md">
            <Group>
              <Select
                label="Filter by Status"
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || "ALL")}
                data={[
                  { value: "ALL", label: "All Statuses" },
                  { value: "REGISTERED", label: "Registered" },
                  { value: "PENDING", label: "Pending Approval" },
                  { value: "ACTIVE", label: "Active" },
                  { value: "SUSPENDED", label: "Suspended" },
                ]}
                style={{ flex: 1 }}
              />
              <TextInput
                label="Search"
                placeholder="Search by name, email, or registration number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 2 }}
              />
            </Group>

            {isLoading ? (
              <Text c="dimmed">Loading vendors...</Text>
            ) : !vendors || vendors.length === 0 ? (
              <Text c="dimmed">No vendors found.</Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Business Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Business Type</Table.Th>
                    <Table.Th>Registration Number</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Documents</Table.Th>
                    <Table.Th>Registered</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vendors.map((vendor) => (
                    <Table.Tr key={vendor.id}>
                      <Table.Td>
                        <Text fw={500}>
                          {vendor.businessName || "Not provided"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{vendor.email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {getBusinessTypeLabel(vendor.businessType)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {vendor.registrationNumber || "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(vendor.status)} size="sm">
                          {vendor.status === "REGISTERED"
                            ? "Registered"
                            : vendor.status === "PENDING"
                            ? "Pending Approval"
                            : vendor.status === "ACTIVE"
                            ? "Active"
                            : "Suspended"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {vendor._count?.documents && vendor._count.documents > 0 ? (
                          <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<IconFileText size={14} />}
                            onClick={() => handleViewDocuments(vendor.id, vendor.businessName || vendor.email)}
                          >
                            {vendor._count.documents} document{vendor._count.documents !== 1 ? 's' : ''}
                          </Button>
                        ) : (
                          <Text size="sm" c="dimmed">0</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {dayjs(vendor.createdAt).format("MMM DD, YYYY")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="View Details">
                          <ActionIcon
                            color="blue"
                            variant="light"
                            onClick={() =>
                              navigate(`/admin/vendors/${vendor.id}`)
                            }
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Documents Modal */}
      <Modal
        opened={documentsModalOpened}
        onClose={() => {
          setDocumentsModalOpened(false);
          setSelectedVendor(null);
          setSelectedVendorName("");
        }}
        title={`Documents - ${selectedVendorName}`}
        size="xl"
      >
        {vendorDetails && vendorDetails.documents && vendorDetails.documents.length > 0 ? (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Document Type</Table.Th>
                <Table.Th>File Name</Table.Th>
                <Table.Th>File Size</Table.Th>
                <Table.Th>Uploaded At</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vendorDetails.documents.map((doc) => (
                <Table.Tr key={doc.id}>
                  <Table.Td>
                    <Text fw={500}>{getDocumentTypeLabel(doc.documentType)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{doc.fileName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{(doc.fileSize / 1024).toFixed(2)} KB</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{dayjs(doc.uploadedAt).format("MMM DD, YYYY")}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Download Document">
                      <ActionIcon
                        color="blue"
                        variant="light"
                        onClick={() => handleDownloadDocument(vendorDetails.id, doc)}
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed">No documents uploaded</Text>
        )}
      </Modal>
    </Container>
  );
}

